/**
 * Plan Mode UI — inline plan controls rendered inside the map screen.
 *
 * Provides:
 *  - Runner bar (bottom bar showing all runners, click to select)
 *  - Plan sidebar (right side, prioritized action list + drag/keyboard reorder + submit)
 *  - Last tick results (collapsible, shows ✓/✗ per action from actionReport)
 *  - Plan pin rendering on canvas (called from map draw loop)
 *
 * All state reads come from planning.ts (getLocalPlan, getSelectedRunner, etc.)
 */
import { fixture } from '../data/fixture';
import type { PlanCondition, RunnerDTO, ActionDTO, ActionReportEntry } from '../data/fixture';
import { t } from '../i18n';
import {
  getLocalPlan, getSelectedRunner, setSelectedRunner,
  getSelectedTeam, setSelectedTeam, getSelectionLabel,
  addPlanEntry, removePlanEntry, reorderPlanEntry, clearPlan,
  addTeamPlanEntries, updatePlanEntryConditions, updatePlanEntryTargetKind, updatePlanEntryTargetRef,
  buildValidationItems, submitCurrentPlan,
  getRunnerPlan,
  resetPlanFromFixture, onPlanChange,
  getUndoEntry, performUndo,
  CONDITION_TYPES,
  getTemplates, saveTemplate, deleteTemplate, loadTemplate,
  getFilteredActionGroups,
} from '../screens/planning';

// ── Constants ────────────────────────────────────────────────────────────────

const ACTION_COLORS: Record<string, string> = {
  extort: '#fca311', patrol: '#06d6a0', recon: 'var(--violet)',
  sabotage: '#e63946', launder: '#06d6a0', bribe: '#f4a261',
  invest: 'var(--cyan)', spy: 'var(--violet)', recruit: '#3498db',
  assign_manager: 'var(--cyan)', recall_manager: '#e63946',
  buy: '#2ecc71', buy_vehicle: '#e67e22', collect: '#fca311',
};

function actionColor(actionId: string): string {
  return ACTION_COLORS[actionId.toLowerCase()] ?? 'var(--cyan)';
}

/** Minimal HTML-escape for interpolated i18n / target-kind strings. */
function escapeHTML(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

/** Human label for a target-kind. Uses i18n key `target_kind.<kind>`, falling
 *  back to the raw kind until the corpllm-i18n catalog ships the label. */
function targetKindLabel(kind: string): string {
  const key = `target_kind.${kind}`;
  const lbl = t(key);
  return lbl === key ? kind : lbl;
}

// ── Plan Mode state ──────────────────────────────────────────────────────────

let planModeActive = false;
let sidebarEl: HTMLElement | null = null;
let runnerBarEl: HTMLElement | null = null;
let unsubPlanChange: (() => void) | null = null;
let runnerBarChangeUnsub: (() => void) | null = null;

export function isPlanModeActive(): boolean { return planModeActive; }

// ── Detail Panel (conditions editor, impact preview, delete) ────────────────

let detailPanelEl: HTMLElement | null = null;
let detailEntryIdx: number | null = null;

function openDetailPanel(entryIdx: number): void {
  closeDetailPanel();
  const plan = getLocalPlan();
  if (entryIdx < 0 || entryIdx >= plan.length) return;
  detailEntryIdx = entryIdx;

  const entry = plan[entryIdx];
  const runners = fixture.player.runners ?? [];
  const runner = runners.find(r => r.id === entry.runner);
  const color = actionColor(entry.action);
  const editConditions = [...entry.conditions];

  const panel = document.createElement('div');
  panel.id = 'plan-detail-panel';
  panel.setAttribute('role', 'dialog');
  panel.setAttribute('aria-modal', 'true');
  panel.setAttribute('aria-label', t('planui.detail_title'));
  panel.style.cssText = `
    position:fixed; top:50%; right:340px; transform:translateY(-50%);
    width:320px; max-height:80vh; overflow-y:auto; z-index:9500;
    background:rgba(10,12,20,0.98); border:1px solid rgba(var(--ac),0.4);
    border-radius:6px; font-family:var(--mono); box-shadow:0 8px 32px rgba(0,0,0,0.6);
  `;

  function renderPanel(): void {
    const currentPlan = getLocalPlan();
    const currentEntry = detailEntryIdx !== null && detailEntryIdx < currentPlan.length ? currentPlan[detailEntryIdx] : null;
    if (!currentEntry) { closeDetailPanel(); return; }

    // #211 Ziel-Picker — only when this action defines target-kinds (server SSoT).
    const actionDef = (fixture.player.actions ?? []).find(a => a.id === currentEntry.action);
    const targetKinds = actionDef?.targets ?? [];
    const targetPickerHTML = targetKinds.length ? `
        <div style="color:rgba(255,255,255,0.5);font-size:10px;text-transform:uppercase;letter-spacing:1px;margin:4px 0 6px">${escapeHTML(t('planui.target_kind'))}</div>
        <select id="pd-target-kind" aria-label="${escapeHTML(t('planui.target_kind'))}" style="width:100%;background:rgba(255,255,255,0.06);border:1px solid rgba(var(--ac),0.25);color:#e0e8f0;font-size:11px;padding:4px;border-radius:3px;font-family:var(--mono);margin-bottom:10px">
          <option value="" ${!currentEntry.targetKind ? 'selected' : ''}>${escapeHTML(t('planui.target_generic'))}</option>
          ${targetKinds.map(tk => {
            const meta = [tk.cost ? `$${tk.cost}` : '', tk.cooldownHours ? `${tk.cooldownHours}h` : ''].filter(Boolean).join(' · ');
            const text = meta ? `${targetKindLabel(tk.kind)} · ${meta}` : targetKindLabel(tk.kind);
            return `<option value="${escapeHTML(tk.kind)}" ${currentEntry.targetKind === tk.kind ? 'selected' : ''}>${escapeHTML(text)}</option>`;
          }).join('')}
        </select>` : '';

    // #213 cop_patrol target-ref sub-picker — only this kind needs per-entity
    // targeting; appears once cop_patrol is chosen. Lists visible Local patrols
    // homed at the entry's node; default "any" → server picks first eligible.
    const patrols = currentEntry.targetKind === 'cop_patrol'
      ? (fixture.lawUnits ?? []).filter(u => u.homeNodeId === currentEntry.target)
      : [];
    const unitPickerHTML = currentEntry.targetKind === 'cop_patrol' ? `
        <div style="color:rgba(255,255,255,0.5);font-size:10px;text-transform:uppercase;letter-spacing:1px;margin:4px 0 6px">${escapeHTML(t('planui.target_unit'))}</div>
        <select id="pd-target-ref" aria-label="${escapeHTML(t('planui.target_unit'))}" style="width:100%;background:rgba(255,255,255,0.06);border:1px solid rgba(var(--ac),0.25);color:#e0e8f0;font-size:11px;padding:4px;border-radius:3px;font-family:var(--mono);margin-bottom:10px">
          <option value="" ${!currentEntry.targetRef ? 'selected' : ''}>${escapeHTML(t('planui.target_unit_any'))}</option>
          ${patrols.map(u => {
            const ref = `lawunit:${u.id}`;
            const text = `${u.id} · ${u.state}${u.bribedByMe ? ' ✓' : ''}`;
            return `<option value="${escapeHTML(ref)}" ${currentEntry.targetRef === ref ? 'selected' : ''}>${escapeHTML(text)}</option>`;
          }).join('')}
        </select>` : '';

    panel.innerHTML = `
      <div style="display:flex;align-items:center;justify-content:space-between;padding:10px 14px;border-bottom:1px solid rgba(var(--ac),0.15)">
        <span style="color:var(--cyan);font-size:12px;font-weight:600;letter-spacing:1px">${t('planui.detail_title')}</span>
        <button id="pd-close" style="background:none;border:none;color:rgba(255,255,255,0.4);font-size:16px;cursor:pointer">✕</button>
      </div>
      <div style="padding:12px 14px">
        <div style="display:flex;align-items:center;gap:8px;margin-bottom:12px">
          <span style="color:#e0e8f0;font-weight:600">${runner?.name ?? entry.runner}</span>
          <span style="padding:1px 6px;border-radius:3px;font-size:11px;background:${color}22;border:1px solid ${color}44;color:${color}">${entry.action}</span>
          <span style="color:rgba(255,255,255,0.4);font-size:11px">${entry.sector}${entry.target ? ` → ${entry.target}` : ''}</span>
        </div>
        ${targetPickerHTML}
        ${unitPickerHTML}
        <div style="color:rgba(255,255,255,0.5);font-size:10px;text-transform:uppercase;letter-spacing:1px;margin-bottom:6px">${t('planui.conditions')}</div>
        <div id="pd-conditions"></div>
        <button id="pd-add-cond" style="background:none;border:1px dashed rgba(var(--ac),0.3);color:rgba(var(--ac),0.6);font-size:11px;padding:4px 8px;cursor:pointer;border-radius:3px;margin-top:6px;font-family:var(--mono)">+ ${t('planui.add_condition')}</button>
        <div id="pd-impact" style="margin-top:12px"></div>
      </div>
      <div style="padding:10px 14px;border-top:1px solid rgba(255,255,255,0.06);display:flex;gap:8px">
        <button id="pd-save" style="flex:1;padding:6px;background:rgba(var(--ac),0.1);border:1px solid rgba(var(--ac),0.4);color:var(--cyan);font-size:11px;border-radius:3px;cursor:pointer;font-family:var(--mono)">${t('planui.save_conditions')}</button>
        <button id="pd-delete" style="padding:6px 12px;background:rgba(230,57,70,0.08);border:1px solid rgba(230,57,70,0.3);color:var(--red);font-size:11px;border-radius:3px;cursor:pointer;font-family:var(--mono)">${t('planui.delete_action')}</button>
      </div>
    `;

    // Render condition rows
    const condContainer = panel.querySelector('#pd-conditions') as HTMLElement;
    renderConditionRows(condContainer, editConditions);

    // Render impact preview
    const impactEl = panel.querySelector('#pd-impact') as HTMLElement;
    renderImpactPreview(impactEl, editConditions, entry.runner, entry.sector);

    // Event handlers
    panel.querySelector('#pd-close')!.addEventListener('click', closeDetailPanel);
    panel.querySelector('#pd-add-cond')!.addEventListener('click', () => {
      editConditions.push({ type: CONDITION_TYPES[0], op: '<', value: 50 });
      renderConditionRows(condContainer, editConditions);
      renderImpactPreview(impactEl, editConditions, entry.runner, entry.sector);
    });
    panel.querySelector('#pd-save')!.addEventListener('click', () => {
      if (detailEntryIdx !== null) {
        updatePlanEntryConditions(detailEntryIdx, editConditions);
        closeDetailPanel();
      }
    });
    panel.querySelector('#pd-delete')!.addEventListener('click', () => {
      if (detailEntryIdx !== null) {
        removePlanEntry(detailEntryIdx);
        closeDetailPanel();
      }
    });

    // #211 Ziel-Picker — apply the chosen target-kind immediately (sidebar
    // re-renders via notify()); empty value clears back to the generic target.
    const tkSel = panel.querySelector('#pd-target-kind') as HTMLSelectElement | null;
    tkSel?.addEventListener('change', () => {
      if (detailEntryIdx === null) return;
      updatePlanEntryTargetKind(detailEntryIdx, tkSel.value);
      // #213 — targetRef is cop_patrol-specific; drop a stale ref when the kind
      // changes, then re-render so the patrol sub-picker shows/hides.
      if (tkSel.value !== 'cop_patrol') updatePlanEntryTargetRef(detailEntryIdx, '');
      renderPanel();
    });

    // #213 cop_patrol sub-picker — set/clear the specific patrol (targetRef).
    const refSel = panel.querySelector('#pd-target-ref') as HTMLSelectElement | null;
    refSel?.addEventListener('change', () => {
      if (detailEntryIdx !== null) updatePlanEntryTargetRef(detailEntryIdx, refSel.value);
    });
  }

  function renderConditionRows(container: HTMLElement, conds: PlanCondition[]): void {
    container.innerHTML = '';
    for (let ci = 0; ci < conds.length; ci++) {
      const c = conds[ci];
      const row = document.createElement('div');
      row.style.cssText = 'display:flex;align-items:center;gap:4px;padding:3px 0;font-size:11px';
      row.innerHTML = `
        <select class="pd-type" style="background:rgba(255,255,255,0.06);border:1px solid rgba(255,255,255,0.1);color:#e0e8f0;font-size:10px;padding:2px;border-radius:2px;font-family:var(--mono)">
          ${CONDITION_TYPES.map(ct => `<option value="${ct}" ${ct === c.type ? 'selected' : ''}>${ct}</option>`).join('')}
        </select>
        <input class="pd-val" type="number" value="${c.value ?? ''}" style="width:50px;background:rgba(255,255,255,0.06);border:1px solid rgba(255,255,255,0.1);color:#e0e8f0;font-size:10px;padding:2px 4px;border-radius:2px;font-family:var(--mono);text-align:center" />
        <button class="pd-rm-cond" style="background:none;border:none;color:rgba(255,255,255,0.3);cursor:pointer;font-size:12px;padding:0 2px">✕</button>
      `;
      const typeSelect = row.querySelector('.pd-type') as HTMLSelectElement;
      const valInput = row.querySelector('.pd-val') as HTMLInputElement;
      const rmBtn = row.querySelector('.pd-rm-cond')!;

      typeSelect.addEventListener('change', () => {
        conds[ci] = { ...conds[ci], type: typeSelect.value };
        renderImpactPreview(container.parentElement!.querySelector('#pd-impact') as HTMLElement, conds, entry.runner, entry.sector);
      });
      valInput.addEventListener('input', () => {
        conds[ci] = { ...conds[ci], value: parseFloat(valInput.value) || 0 };
        renderImpactPreview(container.parentElement!.querySelector('#pd-impact') as HTMLElement, conds, entry.runner, entry.sector);
      });
      rmBtn.addEventListener('click', () => {
        conds.splice(ci, 1);
        renderConditionRows(container, conds);
        renderImpactPreview(container.parentElement!.querySelector('#pd-impact') as HTMLElement, conds, entry.runner, entry.sector);
      });
      container.appendChild(row);
    }
  }

  function renderImpactPreview(container: HTMLElement, conds: PlanCondition[], runnerId: string, sectorId: string): void {
    if (conds.length === 0) { container.innerHTML = ''; return; }
    const r = (fixture.player.runners ?? []).find(r => r.id === runnerId);
    const sec = (fixture.sectors ?? []).find(s => s.sectorId === sectorId);
    const scouted = (fixture.player.scoutedSectors ?? []).includes(sectorId);

    const lines: string[] = [];
    for (const c of conds) {
      let currentVal: number | string | null = null;
      let known = true;
      if (c.type.startsWith('heat_') && r) currentVal = r.heat;
      else if (c.type.startsWith('loy_') && r) currentVal = r.loy;
      else if (c.type.startsWith('hp_') && r) currentVal = r.hp;
      else if (c.type.startsWith('cash_')) currentVal = fixture.player.cleanCash + fixture.player.dirtyCash;
      else if (c.type.startsWith('sector_') && sec) { currentVal = sec.heat; if (!scouted) known = false; }
      else if (c.type.startsWith('day_')) currentVal = fixture.day;
      else known = false;

      if (!known) {
        lines.push(`<span style="color:rgba(255,255,255,0.3)">👁‍🗨 ${c.type}: ${t('planui.impact_unknown')}</span>`);
      } else if (currentVal !== null) {
        const threshold = Number(c.value) || 0;
        const matches = c.type.includes('below') || c.type.includes('before') ? currentVal < threshold
          : c.type.includes('above') || c.type.includes('after') ? currentVal > threshold
          : currentVal === threshold;
        const col = matches ? '#fca311' : '#06d6a0';
        const icon = matches ? '⚡' : '✓';
        lines.push(`<span style="color:${col}">${icon} ${c.type}: ${currentVal} ${matches ? t('planui.impact_matches') : t('planui.impact_no_match')}</span>`);
      }
    }
    container.innerHTML = lines.length > 0 ? `
      <div style="color:rgba(255,255,255,0.5);font-size:10px;text-transform:uppercase;letter-spacing:1px;margin-bottom:4px">${t('planui.impact_title')}</div>
      ${lines.map(l => `<div style="font-size:10px;padding:1px 0">${l}</div>`).join('')}
      <div style="font-size:9px;color:rgba(255,255,255,0.2);margin-top:4px;font-style:italic">${t('planui.impact_disclaimer')}</div>
    ` : '';
  }

  renderPanel();
  document.body.appendChild(panel);
  detailPanelEl = panel;

  // Focus trap + Escape
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') closeDetailPanel();
  };
  document.addEventListener('keydown', onKey);

  // Click outside closes
  const onOutside = (e: MouseEvent) => {
    if (detailPanelEl && !detailPanelEl.contains(e.target as Node)) closeDetailPanel();
  };
  setTimeout(() => document.addEventListener('click', onOutside), 0);

  // Cleanup refs
  panel.addEventListener('remove', () => {
    document.removeEventListener('keydown', onKey);
    document.removeEventListener('click', onOutside);
  });
}

function closeDetailPanel(): void {
  if (detailPanelEl) {
    detailPanelEl.remove();
    detailPanelEl = null;
    detailEntryIdx = null;
  }
}

// ── Undo Toast ──────────────────────────────────────────────────────────────

let undoToastEl: HTMLElement | null = null;

function showUndoToast(): void {
  removeUndoToast();
  const undo = getUndoEntry();
  if (!undo) return;

  const toast = document.createElement('div');
  toast.setAttribute('role', 'status');
  toast.setAttribute('aria-live', 'polite');
  toast.style.cssText = `
    position:fixed; bottom:80px; left:50%; transform:translateX(-50%); z-index:9600;
    background:rgba(10,12,20,0.95); border:1px solid rgba(var(--ac),0.4);
    border-radius:6px; padding:8px 16px; font-family:var(--mono); font-size:12px;
    display:flex; align-items:center; gap:12px; box-shadow:0 4px 16px rgba(0,0,0,0.5);
    color:#e0e8f0;
  `;
  toast.innerHTML = `
    <span>${t('planui.undo_removed', { action: undo.entry.action })}</span>
    <button id="undo-btn" style="background:rgba(var(--ac),0.15);border:1px solid rgba(var(--ac),0.4);color:var(--cyan);padding:4px 10px;border-radius:3px;cursor:pointer;font-family:var(--mono);font-size:11px">${t('planui.undo')}</button>
  `;
  toast.querySelector('#undo-btn')!.addEventListener('click', () => {
    performUndo();
    removeUndoToast();
  });

  document.body.appendChild(toast);
  undoToastEl = toast;

  // Auto-remove after 4s
  setTimeout(() => { if (undoToastEl === toast) removeUndoToast(); }, 4000);
}

function removeUndoToast(): void {
  if (undoToastEl) { undoToastEl.remove(); undoToastEl = null; }
}

// Global Ctrl+Z handler for undo
function onGlobalKeydown(e: KeyboardEvent): void {
  if (e.key === 'z' && (e.ctrlKey || e.metaKey) && getUndoEntry()) {
    e.preventDefault();
    performUndo();
    removeUndoToast();
  }
}

// ── Runner Bar (bottom of map — always visible) ─────────────────────────────

/**
 * Mount the runner bar into the map root. Called once from renderMap(),
 * independent of plan mode. Clicking a runner/team here auto-opens plan mode.
 */
export function mountRunnerBar(mapRoot: HTMLElement): void {
  if (runnerBarEl) return; // already mounted
  runnerBarMapRoot = mapRoot;
  runnerBarEl = createRunnerBar(mapRoot);
  mapRoot.appendChild(runnerBarEl);
  // Keep runner bar in sync with plan changes
  runnerBarChangeUnsub = onPlanChange(() => {
    if (runnerBarEl) renderRunnerChips(runnerBarEl);
  });
}

/** Remove the runner bar (called when map screen unmounts). */
export function unmountRunnerBar(): void {
  if (runnerBarChangeUnsub) { runnerBarChangeUnsub(); runnerBarChangeUnsub = null; }
  if (runnerBarEl) { runnerBarEl.remove(); runnerBarEl = null; }
  runnerBarMapRoot = null;
}

function createRunnerBar(_mapRoot: HTMLElement): HTMLElement {
  const bar = document.createElement('div');
  bar.id = 'plan-runner-bar';
  bar.style.cssText = `
    position:absolute; bottom:0; left:0; right:0; z-index:200;
    background:rgba(10,12,20,0.95); border-top:1px solid rgba(var(--ac),0.3);
    display:flex; align-items:center; gap:6px; padding:8px 16px;
    overflow-x:auto; font-family:var(--mono);
  `;
  renderRunnerChips(bar);
  return bar;
}

/** Reference to the map root element, set when runner bar is mounted. */
let runnerBarMapRoot: HTMLElement | null = null;

function renderRunnerChips(bar: HTMLElement): void {
  bar.innerHTML = '';
  const runners = fixture.player.runners ?? [];
  const teams = fixture.player.teams ?? [];
  const plan = getLocalPlan();
  const assignedIds = new Set(plan.map(e => e.runner));
  const selectedRunner = getSelectedRunner();
  const selectedTeam = getSelectedTeam();

  /** Selecting a runner/team auto-opens plan mode if not yet active. */
  function ensurePlanMode(): void {
    if (!planModeActive && runnerBarMapRoot) {
      openPlanMode(runnerBarMapRoot);
      // Update plan toggle button in toolbar
      const toggle = runnerBarMapRoot.querySelector<HTMLElement>('#plan-toggle');
      if (toggle) toggle.classList.add('active');
    }
  }

  // ─�� Team chips (before individual runners) ─────────────────────────────
  for (const team of teams) {
    if (team.members.length === 0) continue;
    const isSelected = team.name === selectedTeam;
    // Count how many members have actions
    const memberIds = team.members
      .map(name => runners.find(r => r.name === name)?.id)
      .filter((id): id is string => !!id);
    const assignedCount = memberIds.filter(id => assignedIds.has(id)).length;

    const chip = document.createElement('div');
    chip.style.cssText = `
      display:inline-flex; align-items:center; gap:5px;
      padding:5px 12px; border-radius:20px; font-size:12px;
      cursor:pointer;
      background:${isSelected ? 'rgba(155,93,229,0.2)' : 'rgba(155,93,229,0.06)'};
      border:1px solid ${isSelected ? 'var(--violet)' : 'rgba(155,93,229,0.25)'};
      color:${isSelected ? 'var(--violet)' : '#c8b8e8'};
      transition:all 0.12s; white-space:nowrap;
    `;
    chip.innerHTML = `
      <span style="font-size:11px">&#9733;</span>
      <span>Team ${team.name} (${team.members.length})</span>
      ${assignedCount > 0 ? `<span style="color:var(--violet);font-size:10px">${assignedCount}/${team.members.length}</span>` : ''}
    `;
    chip.addEventListener('click', () => {
      setSelectedTeam(isSelected ? null : team.name);
      if (!isSelected) ensurePlanMode();
    });
    bar.appendChild(chip);
  }

  // ── Separator between teams and individual runners ─────────────────────
  if (teams.some(t => t.members.length > 0)) {
    const sep = document.createElement('div');
    sep.style.cssText = 'width:1px;height:24px;background:rgba(255,255,255,0.1);margin:0 4px;flex-shrink:0';
    bar.appendChild(sep);
  }

  // ── Individual runner chips ────────────────────────────────────────────
  for (const r of runners) {
    const chip = document.createElement('div');
    const isSelected = r.id === selectedRunner;
    const isArrested = r.status === 'arrested';
    const hasAction = assignedIds.has(r.id);

    chip.style.cssText = `
      display:inline-flex; align-items:center; gap:5px;
      padding:5px 12px; border-radius:20px; font-size:12px;
      cursor:${isArrested ? 'not-allowed' : 'pointer'};
      opacity:${isArrested ? '0.4' : '1'};
      background:${isSelected ? 'rgba(var(--ac),0.15)' : 'rgba(255,255,255,0.04)'};
      border:1px solid ${isSelected ? 'var(--cyan)' : 'rgba(255,255,255,0.1)'};
      color:${isSelected ? 'var(--cyan)' : '#e0e8f0'};
      transition:all 0.12s; white-space:nowrap;
    `;

    const dotColor = isArrested ? '#e63946' : r.status === 'injured' ? '#fca311' : '#06d6a0';
    chip.innerHTML = `
      <span style="width:7px;height:7px;border-radius:50%;background:${dotColor};flex-shrink:0"></span>
      <span>${r.name}</span>
      ${hasAction ? '<span style="color:var(--cyan);font-size:10px">●</span>' : ''}
    `;

    if (!isArrested) {
      chip.addEventListener('click', () => {
        setSelectedRunner(isSelected ? null : r.id);
        if (!isSelected) ensurePlanMode();
      });
      chip.addEventListener('contextmenu', (ev) => {
        ev.preventDefault();
        showRunnerQuickActions(ev, r);
      });
    }
    bar.appendChild(chip);
  }
}

// ── Sidebar (right side, prioritized action list + controls) ────────────────

function createSidebar(_mapRoot: HTMLElement): HTMLElement {
  const panel = document.createElement('div');
  panel.id = 'plan-sidebar';
  panel.style.cssText = `
    width:320px; flex-shrink:0; position:relative; z-index:201;
    background:rgba(10,12,20,0.95); border-left:1px solid rgba(var(--ac),0.3);
    display:flex; flex-direction:column; font-family:var(--mono);
  `;

  const style = document.createElement('style');
  style.textContent = `
    #plan-sidebar .ps-header { display:flex;align-items:center;justify-content:space-between;padding:10px 14px;border-bottom:1px solid rgba(var(--ac),0.15); }
    #plan-sidebar .ps-title { color:var(--cyan);font-size:12px;font-weight:600;letter-spacing:1px;text-transform:uppercase; }
    #plan-sidebar .ps-close { background:none;border:none;color:rgba(255,255,255,0.4);font-size:16px;cursor:pointer;padding:0 4px; }
    #plan-sidebar .ps-close:hover { color:var(--red); }
    #plan-sidebar .ps-body { flex:1;overflow-y:auto;padding:10px 14px; }
    #plan-sidebar .ps-row { display:flex;align-items:center;gap:6px;padding:6px 4px;border-bottom:1px solid rgba(255,255,255,0.04);font-size:11px;transition:background 0.1s; }
    #plan-sidebar .ps-row:hover { background:rgba(255,255,255,0.03); }
    #plan-sidebar .ps-row.ps-drag-over { border-top:2px solid var(--cyan); }
    #plan-sidebar .ps-prio { color:rgba(255,255,255,0.3);font-size:10px;min-width:16px;text-align:right; }
    #plan-sidebar .ps-handle { cursor:grab;color:rgba(255,255,255,0.25);font-size:13px;user-select:none;padding:0 2px; }
    #plan-sidebar .ps-handle:hover { color:rgba(255,255,255,0.5); }
    #plan-sidebar .ps-runner { color:#e0e8f0;font-weight:600;min-width:70px; }
    #plan-sidebar .ps-action { padding:1px 6px;border-radius:3px;font-size:11px; }
    #plan-sidebar .ps-sector { color:rgba(255,255,255,0.4); }
    #plan-sidebar .ps-remove { background:none;border:none;color:rgba(255,255,255,0.3);cursor:pointer;font-size:13px;padding:0 2px;margin-left:auto; }
    #plan-sidebar .ps-remove:hover { color:var(--red); }
    #plan-sidebar .ps-footer { padding:10px 14px;border-top:1px solid rgba(var(--ac),0.15); }
    #plan-sidebar .ps-submit { width:100%;padding:8px;background:rgba(var(--ac),0.1);border:1px solid rgba(var(--ac),0.4);color:var(--cyan);font-size:12px;border-radius:3px;cursor:pointer;font-family:var(--mono); }
    #plan-sidebar .ps-submit:hover { background:rgba(var(--ac),0.18); }
    #plan-sidebar .ps-submit:disabled { opacity:0.4;cursor:not-allowed; }
    #plan-sidebar .ps-validation { margin-top:8px; }
    #plan-sidebar .ps-val-item { display:flex;align-items:center;gap:6px;font-size:11px;padding:2px 0; }
    #plan-sidebar .ps-row.ps-focused { outline:1px solid rgba(var(--ac),0.5);outline-offset:-1px;background:rgba(var(--ac),0.05); }
    #plan-sidebar .ps-results-toggle { display:flex;align-items:center;gap:6px;padding:8px 4px;cursor:pointer;user-select:none;font-size:11px;color:rgba(255,255,255,0.5);border-bottom:1px solid rgba(255,255,255,0.06); }
    #plan-sidebar .ps-results-toggle:hover { color:rgba(255,255,255,0.7); }
    #plan-sidebar .ps-result-row { display:flex;align-items:center;gap:6px;padding:3px 4px 3px 20px;font-size:11px; }
    @media (max-width:1024px) {
      #plan-sidebar { width:260px !important; }
    }
  `;
  panel.appendChild(style);

  renderSidebarContent(panel);
  return panel;
}

function renderSidebarContent(panel: HTMLElement): void {
  // Preserve the <style> tag
  const existingStyle = panel.querySelector('style');
  panel.innerHTML = '';
  if (existingStyle) panel.appendChild(existingStyle);

  const plan = getLocalPlan();
  const runners = fixture.player.runners ?? [];
  const runnerMap = new Map(runners.map(r => [r.id, r]));

  // Header
  const header = document.createElement('div');
  header.className = 'ps-header';
  const autoRepeated = fixture.player?.planAutoRepeated === true;
  header.innerHTML = `
    <span class="ps-title">Plan (${plan.length})${autoRepeated ? ` <span style="font-size:9px;font-weight:normal;padding:1px 5px;border-radius:3px;background:rgba(252,163,17,0.12);border:1px solid rgba(252,163,17,0.3);color:#fca311;vertical-align:middle">${t('dashboard.auto_repeated')}</span>` : ''}</span>
    <button class="ps-close" title="${t('planning.ov_close')}">✕</button>
  `;
  header.querySelector('.ps-close')!.addEventListener('click', () => {
    const mapRoot = panel.closest('.screen') as HTMLElement;
    if (mapRoot) togglePlanMode(mapRoot);
  });
  panel.appendChild(header);

  // Body — action rows
  const body = document.createElement('div');
  body.className = 'ps-body';
  body.setAttribute('role', 'list');
  body.tabIndex = 0;

  // ── Last tick results (collapsible) ─────────────────────────────────────
  const report: ActionReportEntry[] = fixture.player?.actionReport ?? [];
  if (report.length > 0) {
    const successes = report.filter(e => e.result === 'SUCCESS' || e.result === 'CRIT_SUCCESS').length;
    const resultColors: Record<string, string> = { SUCCESS: '#06d6a0', CRIT_SUCCESS: 'var(--cyan)', FAIL: '#e63946', CRIT_FAIL: '#e63946', ARRESTED: '#fca311' };
    const resultIcons: Record<string, string> = { SUCCESS: '✓', CRIT_SUCCESS: '✓✓', FAIL: '✗', CRIT_FAIL: '✗✗', ARRESTED: '⚠' };

    const toggle = document.createElement('div');
    toggle.className = 'ps-results-toggle';
    toggle.innerHTML = `<span class="ps-results-chevron" style="font-size:10px;width:12px">▶</span>
      <span>${t('planui.last_results')}</span>
      <span style="margin-left:auto;color:${successes === report.length ? '#06d6a0' : '#fca311'}">${successes}/${report.length}</span>`;

    const resultsBody = document.createElement('div');
    resultsBody.style.display = 'none';
    for (const e of report) {
      const c = resultColors[e.result] ?? '#888';
      const icon = resultIcons[e.result] ?? '?';
      const delta = [];
      if (e.deltaCash !== 0) delta.push(`<span style="color:${e.deltaCash > 0 ? '#06d6a0' : '#e63946'}">${e.deltaCash > 0 ? '+' : ''}$${e.deltaCash}</span>`);
      if (e.deltaHeat !== 0) delta.push(`<span style="color:#fca311">${e.deltaHeat > 0 ? '+' : ''}${e.deltaHeat}H</span>`);
      const row = document.createElement('div');
      row.className = 'ps-result-row';
      row.innerHTML = `<span style="color:${c};width:16px;text-align:center;font-weight:bold">${icon}</span>
        <span style="color:#e0e8f0;min-width:60px">${e.runner}</span>
        <span style="color:rgba(255,255,255,0.5);flex:1">${e.action}${e.target ? ` → ${e.target}` : ''}</span>
        <span style="font-size:10px">${delta.join(' ')}</span>`;
      resultsBody.appendChild(row);
    }

    toggle.addEventListener('click', () => {
      const open = resultsBody.style.display !== 'none';
      resultsBody.style.display = open ? 'none' : '';
      toggle.querySelector('.ps-results-chevron')!.textContent = open ? '▶' : '▼';
    });
    body.appendChild(toggle);
    body.appendChild(resultsBody);
  }

  // ── Action list ─────────────────────────────────────────────────────────
  if (plan.length === 0) {
    body.innerHTML += `<div style="color:rgba(255,255,255,0.25);font-size:12px;text-align:center;padding:40px 0">
      ${t('planui.empty_hint')}
    </div>`;
  } else {
    let dragSrcIdx: number | null = null;
    let focusedIdx: number | null = null;

    function setFocused(idx: number | null): void {
      body.querySelectorAll('.ps-row.ps-focused').forEach(el => el.classList.remove('ps-focused'));
      focusedIdx = idx;
      if (idx !== null) {
        const row = body.querySelector(`.ps-row[data-idx="${idx}"]`) as HTMLElement | null;
        if (row) { row.classList.add('ps-focused'); row.scrollIntoView({ block: 'nearest' }); }
      }
    }

    // Keyboard reorder: ↑/↓ to move focus, Alt+↑/↓ to reorder
    body.addEventListener('keydown', (e) => {
      if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
      e.preventDefault();
      const len = getLocalPlan().length;
      if (len === 0) return;

      if (focusedIdx === null) { setFocused(0); return; }

      if (e.altKey) {
        // Alt+Arrow = reorder
        const to = e.key === 'ArrowUp' ? focusedIdx - 1 : focusedIdx + 1;
        if (to >= 0 && to < len) {
          reorderPlanEntry(focusedIdx, to);
          // Focus follows the moved entry (reorder triggers re-render via onPlanChange)
        }
      } else {
        // Arrow = move focus
        const next = e.key === 'ArrowUp' ? Math.max(0, focusedIdx - 1) : Math.min(len - 1, focusedIdx + 1);
        setFocused(next);
      }
    });

    for (let i = 0; i < plan.length; i++) {
      const entry = plan[i];
      const runner = runnerMap.get(entry.runner);
      const color = actionColor(entry.action);
      const targetLabel = entry.target ? ` → ${entry.target}` : '';
      const kindLabel = entry.targetKind ? ` <span style="color:var(--cyan)">⊕${escapeHTML(targetKindLabel(entry.targetKind))}${entry.targetRef ? '▸' + escapeHTML(entry.targetRef.replace(/^lawunit:/, '')) : ''}</span>` : '';

      const row = document.createElement('div');
      row.className = 'ps-row';
      row.draggable = true;
      row.dataset.idx = String(i);
      row.setAttribute('role', 'listitem');
      row.setAttribute('aria-label', `${entry.action} — ${entry.sector}${targetLabel}${entry.targetKind ? ' → ' + targetKindLabel(entry.targetKind) : ''}, ${t('planui.priority')} ${i + 1}`);
      const condCount = entry.conditions.length;
      const condBadge = condCount > 0 ? `<span title="${condCount} ${t('planui.conditions')}" style="color:#fca311;font-size:10px;cursor:help">${condCount > 1 ? `⑂${condCount}` : '⑂'}</span>` : '';
      row.innerHTML = `
        <span class="ps-handle" title="${t('planui.drag_hint')}">☰</span>
        <span class="ps-prio">${i + 1}</span>
        <span class="ps-runner">${runner?.name ?? entry.runner}</span>
        <span class="ps-action" style="background:${color}22;border:1px solid ${color}44;color:${color}">${entry.action}</span>
        ${condBadge}
        <span class="ps-sector">${entry.sector}${targetLabel}${kindLabel}</span>
        <button class="ps-remove" data-idx="${i}" title="${t('planning.btn_delete_entry')}">✕</button>
      `;
      if (condCount > 0) row.setAttribute('aria-description', `${condCount} ${t('planui.conditions')}`);
      row.querySelector('.ps-remove')!.addEventListener('click', (e) => {
        e.stopPropagation();
        removePlanEntry(i);
        showUndoToast();
      });
      row.addEventListener('click', (e) => {
        if ((e.target as HTMLElement).closest('.ps-remove') || (e.target as HTMLElement).closest('.ps-handle')) return;
        setFocused(i);
        openDetailPanel(i);
      });

      // Drag & drop reorder
      row.addEventListener('dragstart', (ev) => {
        dragSrcIdx = i;
        row.style.opacity = '0.4';
        ev.dataTransfer!.effectAllowed = 'move';
        ev.dataTransfer!.setData('text/plain', String(i));
      });
      row.addEventListener('dragend', () => {
        row.style.opacity = '1';
        dragSrcIdx = null;
        body.querySelectorAll('.ps-drag-over').forEach(el => el.classList.remove('ps-drag-over'));
      });
      row.addEventListener('dragover', (ev) => {
        ev.preventDefault();
        ev.dataTransfer!.dropEffect = 'move';
        row.classList.add('ps-drag-over');
      });
      row.addEventListener('dragleave', () => {
        row.classList.remove('ps-drag-over');
      });
      row.addEventListener('drop', (ev) => {
        ev.preventDefault();
        row.classList.remove('ps-drag-over');
        const toIdx = parseInt(row.dataset.idx!, 10);
        if (dragSrcIdx !== null && dragSrcIdx !== toIdx) {
          reorderPlanEntry(dragSrcIdx, toIdx);
        }
      });

      body.appendChild(row);
    }
  }
  panel.appendChild(body);

  // Footer — validation + submit
  const footer = document.createElement('div');
  footer.className = 'ps-footer';

  // Validation summary
  const valItems = buildValidationItems(plan);
  const hasErrors = valItems.some(v => v.level === 'error');
  const iconMap = { ok: '✓', warn: '⚠', error: '✕' } as const;
  const colorMap = { ok: '#06d6a0', warn: '#fca311', error: '#e63946' } as const;

  const valDiv = document.createElement('div');
  valDiv.className = 'ps-validation';
  for (const item of valItems) {
    const el = document.createElement('div');
    el.className = 'ps-val-item';
    el.innerHTML = `<span style="color:${colorMap[item.level]}">${iconMap[item.level]}</span><span>${item.text}</span>`;
    valDiv.appendChild(el);
  }
  footer.appendChild(valDiv);

  // Submit button
  const submitBtn = document.createElement('button');
  submitBtn.className = 'ps-submit';
  submitBtn.textContent = t('planning.btn_submit');
  submitBtn.disabled = hasErrors;
  submitBtn.style.marginTop = '10px';

  const statusEl = document.createElement('div');
  statusEl.style.cssText = 'font-size:11px;margin-top:6px;min-height:16px';

  submitBtn.addEventListener('click', async () => {
    const sessionId = sessionStorage.getItem('corpllm_active_session') ?? '';
    submitBtn.disabled = true;
    submitBtn.textContent = '...';
    const result = await submitCurrentPlan(sessionId);
    if (result.ok) {
      statusEl.textContent = t('planui.submitted_short', { id: result.planId ?? '' });
      statusEl.style.color = '#06d6a0';
    } else {
      statusEl.textContent = result.errors?.join(', ') ?? t('planui.error');
      statusEl.style.color = '#e63946';
    }
    submitBtn.disabled = false;
    submitBtn.textContent = t('planning.btn_submit');
  });

  footer.appendChild(submitBtn);

  // Clear button
  const clearBtn = document.createElement('button');
  clearBtn.className = 'ps-submit';
  clearBtn.textContent = t('planui.clear_plan');
  clearBtn.style.cssText = 'margin-top:6px;background:rgba(230,57,70,0.08);border-color:rgba(230,57,70,0.3);color:var(--red);width:100%;padding:6px;font-size:11px;border-radius:3px;cursor:pointer;font-family:var(--mono);border:1px solid rgba(230,57,70,0.3)';
  clearBtn.addEventListener('click', () => {
    if (plan.length > 0) clearPlan();
  });
  footer.appendChild(clearBtn);

  // ── Templates section ───────────────────────────────────────────────────
  const tplDiv = document.createElement('div');
  tplDiv.style.cssText = 'margin-top:10px;border-top:1px solid rgba(255,255,255,0.06);padding-top:8px';

  const tplHeader = document.createElement('div');
  tplHeader.style.cssText = 'display:flex;align-items:center;justify-content:space-between;margin-bottom:6px';
  tplHeader.innerHTML = `<span style="color:rgba(255,255,255,0.4);font-size:10px;text-transform:uppercase;letter-spacing:1px">${t('planui.templates')}</span>`;

  const tplSaveBtn = document.createElement('button');
  tplSaveBtn.style.cssText = 'background:none;border:1px solid rgba(var(--ac),0.3);color:rgba(var(--ac),0.6);font-size:10px;padding:2px 6px;cursor:pointer;border-radius:2px;font-family:var(--mono)';
  tplSaveBtn.textContent = t('planui.save_template');
  tplSaveBtn.disabled = plan.length === 0;
  tplSaveBtn.addEventListener('click', () => {
    const name = prompt(t('planui.template_name_prompt'));
    if (name?.trim()) {
      saveTemplate(name.trim());
      if (sidebarEl) renderSidebarContent(sidebarEl);
    }
  });
  tplHeader.appendChild(tplSaveBtn);
  tplDiv.appendChild(tplHeader);

  const templates = getTemplates();
  if (templates.length > 0) {
    const tplList = document.createElement('div');
    tplList.style.cssText = 'max-height:100px;overflow-y:auto';
    for (let ti = 0; ti < templates.length; ti++) {
      const tpl = templates[ti];
      const tplRow = document.createElement('div');
      tplRow.style.cssText = 'display:flex;align-items:center;gap:6px;padding:2px 0;font-size:10px';
      tplRow.innerHTML = `
        <span style="color:#e0e8f0;flex:1;cursor:pointer;overflow:hidden;text-overflow:ellipsis;white-space:nowrap" title="${t('planui.load_template_hint')}">${tpl.name} (${tpl.entries.length})</span>
        <button class="tpl-del" style="background:none;border:none;color:rgba(255,255,255,0.2);cursor:pointer;font-size:11px;padding:0 2px">✕</button>
      `;
      const nameSpan = tplRow.querySelector('span')!;
      nameSpan.addEventListener('click', () => {
        const rid = getSelectedRunner();
        if (!rid) { alert(t('planui.select_runner_first')); return; }
        loadTemplate(ti, rid);
      });
      tplRow.querySelector('.tpl-del')!.addEventListener('click', () => {
        deleteTemplate(ti);
        if (sidebarEl) renderSidebarContent(sidebarEl);
      });
      tplList.appendChild(tplRow);
    }
    tplDiv.appendChild(tplList);
  }

  footer.appendChild(tplDiv);
  footer.appendChild(statusEl);

  panel.appendChild(footer);
}

// ── Plan Mode toggle ─────────────────────────────────────────────────────────

export function togglePlanMode(mapRoot: HTMLElement): void {
  if (planModeActive) {
    closePlanMode();
  } else {
    openPlanMode(mapRoot);
  }
}

export function openPlanMode(mapRoot: HTMLElement): void {
  if (planModeActive) return;
  planModeActive = true;
  resetPlanFromFixture();

  // Runner bar is already mounted (always visible) — ensure it exists
  if (!runnerBarEl) mountRunnerBar(mapRoot);

  // Sidebar is a flex child of map-area so it shrinks the canvas instead of overlaying
  sidebarEl = createSidebar(mapRoot);
  const mapArea = mapRoot.querySelector('.map-area');
  if (mapArea) {
    mapArea.appendChild(sidebarEl);
  } else {
    mapRoot.appendChild(sidebarEl);
  }

  document.addEventListener('keydown', onGlobalKeydown);

  // Subscribe to plan changes for sidebar re-render
  unsubPlanChange = onPlanChange(() => {
    if (runnerBarEl) renderRunnerChips(runnerBarEl);
    if (sidebarEl) renderSidebarContent(sidebarEl);
  });
}

export function closePlanMode(): void {
  planModeActive = false;
  if (unsubPlanChange) { unsubPlanChange(); unsubPlanChange = null; }
  closeDetailPanel();
  removeUndoToast();
  document.removeEventListener('keydown', onGlobalKeydown);
  // Runner bar stays — it's always visible
  if (sidebarEl) { sidebarEl.remove(); sidebarEl = null; }
  setSelectedRunner(null);
  setSelectedTeam(null);
  // Re-render runner bar to clear selection highlights
  if (runnerBarEl) renderRunnerChips(runnerBarEl);
}

// ── Runner Quick-Actions (right-click on runner chip) ───────────────────────

let quickMenuEl: HTMLElement | null = null;

function showRunnerQuickActions(ev: MouseEvent, runner: RunnerDTO): void {
  closeQuickMenu();
  const menu = document.createElement('div');
  menu.style.cssText = `
    position:fixed; left:${ev.clientX}px; top:${ev.clientY}px; z-index:9500;
    background:rgba(10,12,20,0.98); border:1px solid rgba(var(--ac),0.4);
    border-radius:4px; padding:4px 0; font-family:var(--mono); font-size:11px;
    min-width:180px; box-shadow:0 4px 16px rgba(0,0,0,0.5);
  `;

  function addItem(label: string, handler: () => void, disabled = false): void {
    const item = document.createElement('div');
    item.style.cssText = `padding:5px 12px;color:${disabled ? 'rgba(255,255,255,0.25)' : '#e0e8f0'};cursor:${disabled ? 'not-allowed' : 'pointer'};transition:background 0.1s`;
    item.textContent = label;
    if (!disabled) {
      item.addEventListener('mouseenter', () => { item.style.background = 'rgba(var(--ac),0.08)'; });
      item.addEventListener('mouseleave', () => { item.style.background = 'transparent'; });
      item.addEventListener('click', () => { closeQuickMenu(); handler(); });
    }
    menu.appendChild(item);
  }

  // Open plan for this runner
  addItem(t('planui.qa_open_plan'), () => {
    setSelectedRunner(runner.id);
  });

  // Repeat last plan (from actionHistory)
  const history = runner.actionHistory ?? [];
  addItem(t('planui.qa_repeat_last'), () => {
    if (history.length === 0) return;
    const lastDay = Math.max(...history.map(h => h.day));
    const lastActions = history.filter(h => h.day === lastDay);
    for (const h of lastActions) {
      addPlanEntry({ runner: runner.id, action: h.action.toLowerCase(), sector: h.target });
    }
  }, history.length === 0);

  // Copy plan from another runner
  const otherRunners = (fixture.player.runners ?? []).filter(r => r.id !== runner.id && r.status !== 'arrested');
  if (otherRunners.length > 0) {
    const copyItem = document.createElement('div');
    copyItem.style.cssText = 'padding:5px 12px;color:#e0e8f0;cursor:pointer;transition:background 0.1s;position:relative';
    copyItem.textContent = t('planui.qa_copy_from') + ' ▸';
    copyItem.addEventListener('mouseenter', () => {
      copyItem.style.background = 'rgba(var(--ac),0.08)';
      // Show submenu
      let sub = copyItem.querySelector('.qa-sub') as HTMLElement | null;
      if (!sub) {
        sub = document.createElement('div');
        sub.className = 'qa-sub';
        sub.style.cssText = `position:absolute;left:100%;top:0;background:rgba(10,12,20,0.98);border:1px solid rgba(var(--ac),0.4);border-radius:4px;padding:4px 0;min-width:120px;box-shadow:0 4px 16px rgba(0,0,0,0.5)`;
        for (const or of otherRunners) {
          const subItem = document.createElement('div');
          subItem.style.cssText = 'padding:4px 10px;color:#e0e8f0;cursor:pointer;transition:background 0.1s;font-size:11px';
          subItem.textContent = or.name;
          subItem.addEventListener('mouseenter', () => { subItem.style.background = 'rgba(var(--ac),0.08)'; });
          subItem.addEventListener('mouseleave', () => { subItem.style.background = 'transparent'; });
          subItem.addEventListener('click', () => {
            closeQuickMenu();
            const otherPlan = getRunnerPlan(or.id);
            for (const e of otherPlan) {
              addPlanEntry({ runner: runner.id, action: e.action, sector: e.sector, target: e.target, conditions: [...e.conditions] });
            }
          });
          sub.appendChild(subItem);
        }
        copyItem.appendChild(sub);
      }
    });
    copyItem.addEventListener('mouseleave', () => {
      copyItem.style.background = 'transparent';
      const sub = copyItem.querySelector('.qa-sub');
      if (sub) sub.remove();
    });
    menu.appendChild(copyItem);
  }

  // Load template
  const templates = getTemplates();
  addItem(`${t('planui.qa_load_template')} (${templates.length})`, () => {
    if (templates.length === 0) return;
    setSelectedRunner(runner.id);
    // Load first template as default; user can pick from sidebar
    loadTemplate(0, runner.id);
  }, templates.length === 0);

  // Clear runner plan
  const runnerPlan = getRunnerPlan(runner.id);
  addItem(t('planui.qa_clear_plan'), () => {
    // Remove all entries for this runner (reverse order to preserve indices)
    const plan = getLocalPlan();
    for (let i = plan.length - 1; i >= 0; i--) {
      if (plan[i].runner === runner.id) removePlanEntry(i);
    }
  }, runnerPlan.length === 0);

  document.body.appendChild(menu);
  quickMenuEl = menu;

  // Close on outside click
  const onDoc = (e: MouseEvent) => {
    if (quickMenuEl && !quickMenuEl.contains(e.target as Node)) {
      closeQuickMenu();
      document.removeEventListener('click', onDoc);
    }
  };
  setTimeout(() => document.addEventListener('click', onDoc), 0);
}

function closeQuickMenu(): void {
  if (quickMenuEl) { quickMenuEl.remove(); quickMenuEl = null; }
}

// ── Context menu integration ─────────────────────────────────────────────────

/**
 * Build context menu items for plan mode.
 * Called from map.ts context menu handler when plan mode is active.
 */
export function buildPlanContextItems(sectorId: string, actions: ActionDTO[], opts?: {
  buildingNodeId?: string;
  isOwned?: boolean;
}): HTMLElement[] {
  const items: HTMLElement[] = [];
  const selectedRunner = getSelectedRunner();
  const selectedTeam = getSelectedTeam();

  if (!selectedRunner && !selectedTeam) {
    const hint = document.createElement('div');
    hint.style.cssText = 'padding:4px 6px;color:rgba(255,255,255,0.35);font-size:11px;font-style:italic';
    hint.textContent = t('planui.select_for_action');
    items.push(hint);
    return items;
  }

  const label = getSelectionLabel();

  // Determine target type from context
  const targetType = opts?.buildingNodeId ? 'building' : 'node';

  const groups = getFilteredActionGroups(actions, {
    runnerId: selectedRunner ?? undefined,
    isOwned: opts?.isOwned,
    targetType,
  });

  for (const group of groups) {
    // Category header
    const catHeader = document.createElement('div');
    catHeader.style.cssText = 'padding:3px 6px;color:rgba(255,255,255,0.3);font-size:9px;font-family:var(--mono);letter-spacing:1px;text-transform:uppercase;border-top:1px solid rgba(255,255,255,0.08);margin-top:2px';
    catHeader.textContent = group.cat;
    items.push(catHeader);

    for (const { action: act, avail } of group.items) {
      const btn = document.createElement('div');
      const color = actionColor(act.id);
      const actTooltip = formatActionTooltip(act);

      if (avail.available) {
        btn.style.cssText = `padding:4px 6px;color:${color};cursor:pointer;transition:background 0.1s;font-size:11px`;
        btn.textContent = `${label} → ${act.label}`;
        btn.title = actTooltip;
        btn.addEventListener('mouseenter', () => btn.style.background = `${color}18`);
        btn.addEventListener('mouseleave', () => btn.style.background = 'transparent');
        btn.addEventListener('click', () => {
          if (selectedTeam) {
            addTeamPlanEntries(selectedTeam, {
              action: act.id,
              sector: sectorId,
              target: opts?.buildingNodeId,
            });
          } else if (selectedRunner) {
            addPlanEntry({
              runner: selectedRunner,
              action: act.id,
              sector: sectorId,
              target: opts?.buildingNodeId,
            });
          }
        });
      } else {
        // Dimmed + why-disabled tooltip (prepended to full action summary).
        btn.style.cssText = `padding:4px 6px;color:rgba(255,255,255,0.2);font-size:11px;cursor:default`;
        btn.textContent = `${act.label}`;
        const reason = avail.reason ? t(avail.reason) : '';
        btn.title = reason ? `${reason}\n\n${actTooltip}` : actTooltip;
      }

      items.push(btn);
    }
  }

  if (items.length === 0) {
    const hint = document.createElement('div');
    hint.style.cssText = 'padding:4px 6px;color:rgba(255,255,255,0.35);font-size:11px;font-style:italic';
    hint.textContent = t('planui.no_actions_available');
    items.push(hint);
  }

  return items;
}


// ── Action tooltip ──────────────────────────────────────────────────────────

/**
 * Plain-text tooltip shown on hover over an action row in the plan sidebar.
 * Uses `title=` attribute (browser-native tooltip) so it works on any element
 * without extra hover-state plumbing and stays accessible.
 *
 * Content derived from ActionDTO fields only — keep it honest to what the
 * engine actually dispatches. Effect severity + attr list is rendered from
 * the DTO's v1 effect shape (type / target / severity / attr).
 */
export function formatActionTooltip(act: ActionDTO): string {
  const lines: string[] = [];
  lines.push(`${act.label}  [${act.cat}]`);

  // Header line: cost, duration, cooldown budget hints
  const head: string[] = [];
  if (act.durationMin && act.durationMin > 0) head.push(`${act.durationMin} min`);
  if (act.cost && act.cost > 0) head.push(`$${act.cost}`);
  if (act.maxPerCycle && act.maxPerCycle > 0) head.push(`max ${act.maxPerCycle}×/Zyklus`);
  if (head.length > 0) lines.push(head.join(' · '));

  // Target info
  const target: string[] = [];
  if (act.targetType && act.targetType.length > 0) target.push(`Ziel: ${act.targetType.join('/')}`);
  if (act.targetOwnership && act.targetOwnership.length > 0) target.push(`(${act.targetOwnership.join('/')})`);
  if (target.length > 0) lines.push(target.join(' '));

  // Primary attrs required
  if (act.prereqMinAttr && Object.keys(act.prereqMinAttr).length > 0) {
    const attrs = Object.entries(act.prereqMinAttr).map(([k, v]) => `${k}≥${v}`).join(', ');
    lines.push(`Braucht: ${attrs}`);
  }
  if (act.prereqSkill && act.prereqSkill.length > 0) {
    lines.push(`Skill: ${act.prereqSkill.join(', ')}`);
  }

  // Effects — collapse duplicates by type, keep severity + attr list.
  if (act.effects && act.effects.length > 0) {
    lines.push('');
    lines.push('Effekte:');
    for (const ef of act.effects) {
      const attrs = ef.attr && ef.attr.length > 0 ? ` (${ef.attr.join('+')})` : '';
      const sev = ef.severity ? ` · ${ef.severity}` : '';
      const target = ef.target && ef.target !== 'self' ? ` → ${ef.target}` : '';
      lines.push(`  • ${ef.type}${attrs}${sev}${target}`);
    }
  }

  // Fail branches — show what goes wrong on crit-fail
  if (act.failEffects && act.failEffects.length > 0) {
    const critFails = act.failEffects.filter(f => f.on === 'crit_fail' || f.on === 'fail');
    if (critFails.length > 0) {
      lines.push('');
      lines.push('Risiko bei Fail:');
      for (const ef of critFails) {
        const sev = ef.severity ? ` · ${ef.severity}` : '';
        lines.push(`  • ${ef.type}${sev} (${ef.on})`);
      }
    }
  }

  return lines.join('\n');
}

// ── Canvas plan pin rendering ────────────────────────────────────────────────

/** Action visualization config for canvas rendering. */
export const ACTION_VIS: Record<string, { color: string; glyph: string }> = {
  extort:         { color: '#fca311', glyph: '$' },
  collect:        { color: '#fca311', glyph: '$' },
  patrol:         { color: '#06d6a0', glyph: 'P' },
  recon:          { color: 'var(--violet)', glyph: '?' },
  spy:            { color: 'var(--violet)', glyph: '?' },
  sabotage:       { color: '#e63946', glyph: '!' },
  launder:        { color: '#06d6a0', glyph: '~' },
  assign_manager: { color: 'var(--cyan)', glyph: '★' },
  recall_manager: { color: '#e63946', glyph: '✕' },
  invest:         { color: 'var(--cyan)', glyph: '▲' },
  recruit:        { color: '#3498db', glyph: 'R' },
  buy:            { color: '#2ecc71', glyph: 'B' },
  buy_vehicle:    { color: '#e67e22', glyph: 'V' },
  bribe:          { color: '#f4a261', glyph: '♦' },
};
